import { useState } from 'react'
import { Award, Info, Plus } from 'lucide-react'
import { DateValue, EmptyState, ErrorState, FormDialog, PageHeader, EmployeePicker } from '@/components/common'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { useBreadcrumb } from '@/hooks/useBreadcrumb'
import { useLookup } from '@/hooks/useLookup'
import { toApiProblem } from '@/lib/api/client'
import { useEngagementMutations, useEngagementSettings, useRecognitions } from '../api'

/** People thanking each other, and what — if anything — the points are worth. */
export function RecognitionPage() {
  useBreadcrumb()
  const recognitions = useRecognitions()
  const settings = useEngagementSettings()
  const entities = useLookup<{ id: string; name: string }>('legalEntities')
  const { recognise, saveSettings } = useEngagementMutations()

  const [draft, setDraft] = useState<{
    toEmployeeId: string; valueTag: string; message: string; points: string
  } | null>(null)
  const [settingDraft, setSettingDraft] = useState<{
    pointsEnabled: boolean; pointValue: string; currency: string
  } | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [dialogError, setDialogError] = useState<string | null>(null)

  return (
    <div className="space-y-4">
      <PageHeader
        title="Recognition"
        subtitle="Who thanked whom, and what for."
        actions={
          <div className="flex gap-2">
            <Button
              type="button"
              onClick={() => {
                setError(null)
                setDraft({ toEmployeeId: '', valueTag: '', message: '', points: '' })
              }}
            >
              <Plus className="size-4" aria-hidden="true" />
              Thank somebody
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setError(null)
                setSettingDraft({
                  pointsEnabled: settings.data?.pointsEnabled ?? false,
                  pointValue: settings.data?.pointValue?.toString() ?? '',
                  currency: settings.data?.currency ?? '',
                })
              }}
            >
              Points settings
            </Button>
          </div>
        }
      />

      {error && (
        <p className="rounded-md border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800">
          {error}
        </p>
      )}

      {settings.isSuccess && (
        <p className="flex items-start gap-2 rounded-md border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
          <Info className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
          {settings.data.note}
        </p>
      )}

      <Card>
        {recognitions.isPending && <Skeleton className="m-4 h-24" />}
        {recognitions.isError && (
          <ErrorState error={recognitions.error} onRetry={() => void recognitions.refetch()} />
        )}
        {recognitions.isSuccess && recognitions.data.length === 0 && (
          <EmptyState
            title="Nobody has thanked anybody yet"
            description="Recognition here is a message with a name on it. Points are optional, and worth nothing until somebody says what they are worth."
          />
        )}
        {recognitions.isSuccess && recognitions.data.length > 0 && (
          <ul className="divide-y divide-slate-100">
            {recognitions.data.map((row) => (
              <li key={row.id} className="p-4 text-sm">
                <p className="flex flex-wrap items-center gap-2">
                  <Award className="size-4 text-amber-600" aria-hidden="true" />
                  <span className="font-medium text-[var(--color-heading)]">{row.fromName}</span>
                  <span className="text-slate-600">thanked</span>
                  <span className="font-medium text-[var(--color-heading)]">{row.toName}</span>
                  {row.valueTag && (
                    <span className="rounded bg-slate-100 px-1.5 py-0.5 text-xs text-slate-700">
                      {row.valueTag}
                    </span>
                  )}
                  <span className="text-xs text-slate-500"><DateValue value={row.createdAt} /></span>
                </p>
                <p className="mt-1 text-[var(--color-heading)]">{row.message}</p>
                {row.points != null && (
                  <p className="mt-1 text-xs text-slate-600">
                    {row.points} point(s). {row.pointsNote}
                  </p>
                )}
              </li>
            ))}
          </ul>
        )}
      </Card>

      <FormDialog
        open={draft != null}
        onOpenChange={(open) => { if (!open) { setDraft(null); setDialogError(null) } }}
        title="Thank somebody"
        description="Say what they did. A badge with no words is a badge with no meaning."
        submitLabel="Send it"
        loading={recognise.isPending}
        error={dialogError}
        onSubmit={() => {
          if (!draft) return
          void (async () => {
            setDialogError(null)
            try {
              await recognise.mutateAsync({
                legalEntityId: entities.data?.[0]?.id ?? null,
                toEmployeeId: draft.toEmployeeId,
                valueTag: draft.valueTag || null,
                message: draft.message,
                points: draft.points ? Number(draft.points) : null,
                visibility: 'PUBLIC',
              })
              setDraft(null)
            } catch (caught) {
              setDialogError(toApiProblem(caught)?.detail ?? 'That could not be sent')
            }
          })()
        }}
      >
        {draft && (
          <div className="space-y-3">
            <div>
              <Label htmlFor="recognition-to">Who</Label>
              <EmployeePicker
                  id="recognition-to"
                  value={draft.toEmployeeId}
                  onChange={(employeeId) => setDraft({ ...draft, toEmployeeId: employeeId ?? '' })}
                />
            </div>
            <div>
              <Label htmlFor="recognition-value">What for</Label>
              <Input
                id="recognition-value" value={draft.valueTag}
                onChange={(event) => setDraft({ ...draft, valueTag: event.target.value })}
              />
              <p className="mt-1 text-xs text-slate-600">
                A company value or award name. Nothing ships — these are yours.
              </p>
            </div>
            <div>
              <Label htmlFor="recognition-message">In your words</Label>
              <Input
                id="recognition-message" value={draft.message}
                onChange={(event) => setDraft({ ...draft, message: event.target.value })}
              />
            </div>
            {settings.data?.pointsEnabled && (
              <div>
                <Label htmlFor="recognition-points">Points</Label>
                <Input
                  id="recognition-points" type="number" value={draft.points}
                  onChange={(event) => setDraft({ ...draft, points: event.target.value })}
                />
                <p className="mt-1 text-xs text-slate-600">{settings.data.note}</p>
              </div>
            )}
          </div>
        )}
      </FormDialog>

      <FormDialog
        open={settingDraft != null}
        onOpenChange={(open) => { if (!open) { setSettingDraft(null); setDialogError(null) } }}
        title="Points settings"
        description="A point is a number in this system until somebody attaches money to it."
        submitLabel="Save"
        loading={saveSettings.isPending}
        error={dialogError}
        onSubmit={() => {
          if (!settingDraft) return
          void (async () => {
            setDialogError(null)
            try {
              await saveSettings.mutateAsync({
                pointsEnabled: settingDraft.pointsEnabled,
                pointValue: settingDraft.pointValue ? Number(settingDraft.pointValue) : null,
                currency: settingDraft.currency || null,
              })
              setSettingDraft(null)
            } catch (caught) {
              setDialogError(toApiProblem(caught)?.detail ?? 'Those settings could not be saved')
            }
          })()
        }}
      >
        {settingDraft && (
          <div className="space-y-3">
            <label className="flex items-center gap-2 text-sm">
              <input
                id="points-enabled" type="checkbox" className="size-4"
                checked={settingDraft.pointsEnabled}
                onChange={(event) =>
                  setSettingDraft({ ...settingDraft, pointsEnabled: event.target.checked })}
              />
              Recognition can carry points
            </label>
            {settingDraft.pointsEnabled && (
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <Label htmlFor="point-value">A point is worth</Label>
                  <Input
                    id="point-value" type="number" value={settingDraft.pointValue}
                    onChange={(event) =>
                      setSettingDraft({ ...settingDraft, pointValue: event.target.value })}
                  />
                  <p className="mt-1 text-xs text-slate-600">
                    No default: this is a benefit you are defining.
                  </p>
                </div>
                <div>
                  <Label htmlFor="point-currency">Currency</Label>
                  <Input
                    id="point-currency" maxLength={3} value={settingDraft.currency}
                    onChange={(event) => setSettingDraft({
                      ...settingDraft, currency: event.target.value.toUpperCase() })}
                  />
                </div>
              </div>
            )}
          </div>
        )}
      </FormDialog>
    </div>
  )
}
